import React, { createContext, useContext, useState, useEffect } from 'react';
import { Departamento, Usuario, Tarefa } from '../types';
import { useAuth } from './AuthContext';
import { useKanban } from './KanbanContext';
import departamentoService, { DepartamentoDetalhado } from '../services/departamentoService';
import { obterDepartamentosComDetalhes } from '../services/dashboardService';

interface DepartamentoContextType {
  departamentos: Departamento[];
  departamentoAtual: Departamento | null;
  tarefasDepartamento: Tarefa[];
  usuariosDepartamento: Usuario[];
  ehLiderDepartamento: boolean;
  departamentosLiderados: string[];
  carregando: boolean;
  erro: string;
  obterNomeDepartamento: (departamentoId: string) => string;
  obterDepartamentoPorId: (departamentoId: string) => Departamento | null;
  obterLideresDepartamento: (departamentoId: string) => Usuario[];
  obterUsuariosDepartamento: (departamentoId: string) => Usuario[];
  obterTarefasDepartamento: (departamentoId: string) => Tarefa[];
  adicionarLiderDepartamento: (departamentoId: string, usuarioId: string) => Promise<boolean>;
  removerLiderDepartamento: (departamentoId: string, usuarioId: string) => Promise<boolean>;
  carregarDetalhesDepartamento: (departamentoId: string) => Promise<void>;
  ehLiderDoDepartamento: (departamentoId: string) => boolean;
  carregarLiderancasUsuario: (usuarioId: string) => Promise<void>;
}

const DepartamentoContext = createContext<DepartamentoContextType | undefined>(undefined);

export const DepartamentoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [departamentoAtual, setDepartamentoAtual] = useState<Departamento | null>(null);
  const [departamentoDetalhado, setDepartamentoDetalhado] = useState<DepartamentoDetalhado | null>(null);
  const [tarefasDepartamento, setTarefasDepartamento] = useState<Tarefa[]>([]);
  const [usuariosDepartamento, setUsuariosDepartamento] = useState<Usuario[]>([]);
  const [ehLiderDepartamento, setEhLiderDepartamento] = useState<boolean>(false);
  const [departamentosLiderados, setDepartamentosLiderados] = useState<string[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string>('');
  
  const { usuario } = useAuth();
  const { quadro } = useKanban();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  
  // Carregar todos os departamentos quando o componente for montado
  useEffect(() => {
    const carregarDepartamentos = async () => {
      setCarregando(true);
      try {
        // Tentar usar o endpoint otimizado primeiro (apenas para admin)
        if (usuario?.tipo === 'admin') {
          try {
            const respostaOtimizada = await obterDepartamentosComDetalhes();
            if (respostaOtimizada.success && respostaOtimizada.departamentos) {
              // Converter para formato compatível com Departamento
              const departamentosFormatados = respostaOtimizada.departamentos.map(dept => ({
                id: dept.id,
                nome: dept.nome as any,
                descricao: dept.descricao,
                lideres: [],
                ultima_atualizacao: dept.ultimaAtualizacao,
                ultimaAtualizacao: dept.ultimaAtualizacao,
                tarefas: dept.tarefas
              }));
              setDepartamentos(departamentosFormatados);
              setErro('');
              setCarregando(false);
              return;
            }
          } catch (error) {
            console.warn('Endpoint otimizado falhou, usando fallback:', error);
          }
        }
        
        // Fallback: usar endpoint antigo
        const resposta = await departamentoService.obterTodos();
        if (resposta.success && resposta.departamentos) {
          setDepartamentos(resposta.departamentos);
          setErro('');
        } else {
          throw new Error(resposta.message || 'Erro ao carregar departamentos');
        }
      } catch (error: any) {
        console.error('Erro ao carregar departamentos:', error);
        setErro('Não foi possível carregar os departamentos. Tente novamente mais tarde.');
      } finally {
        setCarregando(false);
      }
    };
    
    carregarDepartamentos();
  }, [usuario]);
  
  // Extraímos todas as tarefas do quadro Kanban
  const extrairTarefas = (): Tarefa[] => {
    return quadro.colunas.flatMap(coluna => coluna.tarefas);
  };
  
  const carregarLiderancasUsuario = async (usuarioId: string) => {
    try {
      const resposta = await departamentoService.obterLiderancasUsuario(usuarioId);
      if (resposta.success && resposta.liderancas) {
        const idsLiderados = resposta.liderancas.map(dep => dep.id);
        setDepartamentosLiderados(idsLiderados);
        if (idsLiderados.length > 0) setEhLiderDepartamento(true);
      } else {
        setDepartamentosLiderados([]);
      }
    } catch (error) {
      setDepartamentosLiderados([]);
    }
  };
  
  // Função para verificar se o usuário é líder de um departamento específico
  const ehLiderDoDepartamento = (departamentoId: string): boolean => {
    return departamentosLiderados.includes(departamentoId);
  };

  useEffect(() => {
    if (usuario && departamentos.length > 0) {
      const depAtual = departamentos.find(dep => dep.id === usuario.departamentoId) || null;
      setDepartamentoAtual(depAtual);
      carregarLiderancasUsuario(usuario.id);
      if (depAtual) carregarDetalhesDepartamento(depAtual.id);
    }
  }, [usuario, departamentos]);
  
  const carregarDetalhesDepartamento = async (departamentoId: string) => {
    setCarregando(true);
    try {
      const resposta = await departamentoService.obterPorId(departamentoId);
      if (resposta.success && resposta.departamento) {
        setDepartamentoDetalhado(resposta.departamento);
        setUsuariosDepartamento(resposta.departamento.membros);
        if (usuario && usuario.departamentoId === departamentoId) {
          setEhLiderDepartamento(usuario.ehLider === true);
        }
        setErro('');
      } else {
        throw new Error(resposta.message || 'Erro ao carregar detalhes do departamento');
      }
    } catch (error: any) {
      setErro('Não foi possível carregar os detalhes do departamento. Tente novamente mais tarde.');
    } finally {
      setCarregando(false);
    }
  };
  
  useEffect(() => {
    if (!departamentoAtual || !usuario) return;

    const todasTarefas = extrairTarefas();

    if (ehLiderDepartamento && departamentosLiderados.length > 0) {
      const tarefasDoDepartamento = todasTarefas.filter((tarefa: Tarefa) => {
        if (tarefa.departamentoId && departamentosLiderados.includes(tarefa.departamentoId)) return true;
        if (usuariosDepartamento.length > 0 && ehLiderDoDepartamento(departamentoAtual.id)) {
          const membrosIds = usuariosDepartamento.map(membro => membro.id);
          return membrosIds.includes(tarefa.responsavel.id) || membrosIds.includes(tarefa.criador.id);
        }
        return false;
      });
      setTarefasDepartamento(tarefasDoDepartamento);
    } else {
      setTarefasDepartamento(todasTarefas.filter((tarefa: Tarefa) => tarefa.responsavel.id === usuario.id));
    }
  }, [departamentoAtual, ehLiderDepartamento, departamentosLiderados, quadro, usuariosDepartamento, usuario, usuarios]);
  
  // Função para obter o nome do departamento pelo ID
  const obterNomeDepartamento = (departamentoId: string): string => {
    const dep = departamentos.find(d => d.id === departamentoId);
    return dep ? dep.nome : 'Departamento não encontrado';
  };
  
  // Função para obter um departamento pelo ID
  const obterDepartamentoPorId = (departamentoId: string): Departamento | null => {
    return departamentos.find(d => d.id === departamentoId) || null;
  };
  
  // Função para obter líderes de um departamento
  const obterLideresDepartamento = (departamentoId: string): Usuario[] => {
    // Filtramos os usuários que pertencem ao departamento e são líderes
    const usuariosDepto = usuariosDepartamento.filter(u => u.departamentoId === departamentoId);
    return usuariosDepto.filter(u => u.ehLider === true);
  };
  
  // Função para obter usuários de um departamento
  const obterUsuariosDepartamento = (departamentoId: string): Usuario[] => {
    // Se temos o departamento detalhado, usamos os membros dele
    if (departamentoDetalhado && departamentoDetalhado.id === departamentoId) {
      return departamentoDetalhado.membros;
    }
    
    // Filtramos os usuários que pertencem ao departamento especificado
    return usuariosDepartamento.filter(u => u.departamentoId === departamentoId || u.departamento_id === departamentoId);
  };
  
  // Função para obter tarefas de um departamento
  const obterTarefasDepartamento = (departamentoId: string): Tarefa[] => {
    const todasTarefas = extrairTarefas();
    const usuariosDepto = obterUsuariosDepartamento(departamentoId);
    const usuariosIds = usuariosDepto.map(u => u.id);
    
    return todasTarefas.filter(tarefa => 
      usuariosIds.includes(tarefa.responsavel.id) || 
      usuariosIds.includes(tarefa.criador.id)
    );
  };
  
  // Função para adicionar um líder ao departamento
  const adicionarLiderDepartamento = async (departamentoId: string, usuarioId: string): Promise<boolean> => {
    try {
      const resposta = await departamentoService.adicionarLider(departamentoId, usuarioId);
      
      if (resposta.success) {
        // Recarregar detalhes do departamento para atualizar a lista de líderes
        await carregarDetalhesDepartamento(departamentoId);
        
        // Atualizar o status de líder do usuário localmente
        setUsuariosDepartamento(prev => 
          prev.map(u => 
            u.id === usuarioId ? { ...u, ehLider: true } : u
          )
        );
        
        // Se o usuário atual foi promovido a líder, atualizar o estado
        if (usuario && usuario.id === usuarioId) {
          setEhLiderDepartamento(true);
        }
        
        return true;
      } else {
        console.error('Erro ao adicionar líder:', resposta.message);
        return false;
      }
    } catch (error: any) {
      console.error('Erro ao adicionar líder:', error);
      return false;
    }
  };
  
  // Função para remover um líder do departamento
  const removerLiderDepartamento = async (departamentoId: string, usuarioId: string): Promise<boolean> => {
    try {
      const resposta = await departamentoService.removerLider(departamentoId, usuarioId);
      
      if (resposta.success) {
        // Recarregar detalhes do departamento para atualizar a lista de líderes
        await carregarDetalhesDepartamento(departamentoId);
        
        // Atualizar o status de líder do usuário localmente
        setUsuariosDepartamento(prev => 
          prev.map(u => 
            u.id === usuarioId ? { ...u, ehLider: false } : u
          )
        );
        
        // Se o usuário atual foi removido como líder, atualizar o estado
        if (usuario && usuario.id === usuarioId) {
          setEhLiderDepartamento(false);
        }
        
        return true;
      } else {
        console.error('Erro ao remover líder:', resposta.message);
        return false;
      }
    } catch (error: any) {
      console.error('Erro ao remover líder:', error);
      return false;
    }
  };
  
  return (
    <DepartamentoContext.Provider
      value={{
        departamentos,
        departamentoAtual,
        tarefasDepartamento,
        usuariosDepartamento,
        ehLiderDepartamento,
        departamentosLiderados,
        carregando,
        erro,
        obterNomeDepartamento,
        obterDepartamentoPorId,
        obterLideresDepartamento,
        obterUsuariosDepartamento,
        obterTarefasDepartamento,
        adicionarLiderDepartamento,
        removerLiderDepartamento,
        carregarDetalhesDepartamento,
        ehLiderDoDepartamento,
        carregarLiderancasUsuario
      }}
    >
      {children}
    </DepartamentoContext.Provider>
  );
};

export const useDepartamento = (): DepartamentoContextType => {
  const context = useContext(DepartamentoContext);
  if (context === undefined) {
    throw new Error('useDepartamento deve ser usado dentro de um DepartamentoProvider');
  }
  return context;
};
